import { useState, useCallback, useRef } from 'react';
import type { SquareComment } from '../types';
import {
  getSquareComments,
  getSquareCommentCount,
  createSquareComment,
  deleteSquareComment,
} from '../apiClient';

interface UseSquareCommentsOptions {
  postId: string;
  onCommentCountChange?: (count: number) => void;
}

interface UseSquareCommentsReturn {
  comments: SquareComment[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => Promise<void>;
  addComment: (content: string) => Promise<boolean>;
  removeComment: (commentId: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

/**
 * Hook for managing square comments with cursor-based lazy loading
 */
export function useSquareComments({
  postId,
  onCommentCountChange,
}: UseSquareCommentsOptions): UseSquareCommentsReturn {
  const [comments, setComments] = useState<SquareComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cursorRef = useRef<string | null>(null);
  const isLoadingRef = useRef(false);

  /**
   * Load initial or more comments
   */
  const loadMore = useCallback(async () => {
    // Prevent concurrent loads
    if (isLoadingRef.current || !hasMore) return;

    isLoadingRef.current = true;
    const isInitialLoad = comments.length === 0;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const response = await getSquareComments(postId, {
        cursor: cursorRef.current || undefined,
        limit: 20,
      });

      setComments((prev) =>
        isInitialLoad ? response.comments : [...prev, ...response.comments]
      );
      cursorRef.current = response.nextCursor;
      setHasMore(response.hasMore);

      // Fetch and notify comment count on initial load
      if (isInitialLoad && onCommentCountChange) {
        try {
          const countResponse = await getSquareCommentCount(postId);
          onCommentCountChange(countResponse.count);
        } catch (countErr) {
          console.error('[useSquareComments] Failed to get comment count:', countErr);
        }
      }
    } catch (err) {
      console.error('[useSquareComments] Failed to load comments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load comments');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isLoadingRef.current = false;
    }
  }, [postId, hasMore, comments.length, onCommentCountChange]);

  /**
   * Refresh comments (reset and reload)
   */
  const refresh = useCallback(async () => {
    setComments([]);
    cursorRef.current = null;
    setHasMore(true);
    isLoadingRef.current = false;

    // Wait for state to update, then load
    await new Promise((resolve) => setTimeout(resolve, 0));
    await loadMore();
  }, [loadMore]);

  /**
   * Add a new comment
   */
  const addComment = useCallback(
    async (content: string): Promise<boolean> => {
      if (!content.trim()) return false;

      try {
        const response = await createSquareComment({
          cloudPostId: postId,
          content: content.trim(),
        });

        // Add new comment to the top of the list
        setComments((prev) => [response.comment, ...prev]);

        // Notify parent of count change
        onCommentCountChange?.(response.newCount);

        return true;
      } catch (err) {
        console.error('[useSquareComments] Failed to add comment:', err);
        setError(err instanceof Error ? err.message : 'Failed to add comment');
        return false;
      }
    },
    [postId, onCommentCountChange]
  );

  /**
   * Remove a comment
   */
  const removeComment = useCallback(
    async (commentId: string): Promise<boolean> => {
      try {
        const response = await deleteSquareComment(commentId);

        // Remove comment from list
        setComments((prev) => prev.filter((c) => c.id !== commentId));

        // Notify parent of count change
        onCommentCountChange?.(response.newCount);

        return true;
      } catch (err) {
        console.error('[useSquareComments] Failed to delete comment:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete comment');
        return false;
      }
    },
    [onCommentCountChange]
  );

  return {
    comments,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    addComment,
    removeComment,
    refresh,
  };
}
